import React, { useContext, useState, useEffect, useCallback } from "react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import {
  Clock,
  Star,
  ClipboardPaste,
  History,
  Check,
  AlertCircle,
  Dumbbell,
  Trash2,
} from "lucide-react";
import { getSessions, deleteSession } from "../../services/api";
import { deserializeSession } from "../../utils/sessionShare";
import { formatDuration, calculateTotalDuration } from "../../utils/time";

const LoadSessionDialog = ({ open, onOpenChange }) => {
  const { loadSessionFromData } = useContext(WorkoutContext);
  const { isAuthenticated } = useAuth();

  const [activeTab, setActiveTab] = useState("history");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clipboardText, setClipboardText] = useState("");
  const [clipboardError, setClipboardError] = useState(null);
  const [clipboardValid, setClipboardValid] = useState(false);

  // Fetch sessions when dialog opens
  useEffect(() => {
    if (!open) return;
    loadSessions();
  }, [open, isAuthenticated]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      if (isAuthenticated) {
        const data = await getSessions({ limit: 50 });
        setSessions(data.sessions || data || []);
      } else {
        const stored = JSON.parse(localStorage.getItem("completedSessions") || "[]");
        setSessions(stored);
      }
    } catch (err) {
      console.error("Failed to load sessions:", err);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = useCallback((session) => {
    if (session.plan_data) {
      loadSessionFromData(session.plan_data);
      onOpenChange(false);
    }
  }, [loadSessionFromData, onOpenChange]);

  const handleDeleteSession = useCallback(async (e, session) => {
    e.stopPropagation();
    try {
      if (isAuthenticated && session.id && !String(session.id).startsWith("local-")) {
        await deleteSession(session.id);
      } else {
        const stored = JSON.parse(localStorage.getItem("completedSessions") || "[]");
        const updated = stored.filter((s) => s.id !== session.id);
        localStorage.setItem("completedSessions", JSON.stringify(updated));
      }
      setSessions((prev) => prev.filter((s) => s.id !== session.id));
    } catch (err) {
      console.error("Failed to delete session:", err);
    }
  }, [isAuthenticated]);

  const handleToggleFavourite = useCallback(async (e, session) => {
    e.stopPropagation();
    const newFav = !session.is_favourite;
    setSessions((prev) =>
      prev.map((s) => (s.id === session.id ? { ...s, is_favourite: newFav } : s))
    );
    // Persist locally
    if (!isAuthenticated || String(session.id).startsWith("local-")) {
      const stored = JSON.parse(localStorage.getItem("completedSessions") || "[]");
      const updated = stored.map((s) => (s.id === session.id ? { ...s, is_favourite: newFav } : s));
      localStorage.setItem("completedSessions", JSON.stringify(updated));
    }
  }, [isAuthenticated]);

  // Clipboard validation
  useEffect(() => {
    if (!clipboardText.trim()) {
      setClipboardError(null);
      setClipboardValid(false);
      return;
    }
    const plan = deserializeSession(clipboardText);
    if (plan) {
      setClipboardValid(true);
      setClipboardError(null);
    } else {
      setClipboardValid(false);
      setClipboardError("Could not parse session. Check the YAML format.");
    }
  }, [clipboardText]);

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setClipboardText(text);
    } catch {
      setClipboardError("Unable to read clipboard. Try pasting manually.");
    }
  };

  const handleLoadFromClipboard = () => {
    const plan = deserializeSession(clipboardText);
    if (plan) {
      loadSessionFromData(plan);
      onOpenChange(false);
    }
  };

  const historySessions = sessions.filter((s) => s.plan_data);
  const favouriteSessions = historySessions.filter((s) => s.is_favourite);

  const SessionList = ({ items }) => {
    if (loading) {
      return (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No sessions found.</p>
        </div>
      );
    }
    return (
      <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
        {items.map((session) => {
          const label = session.plan_data?.label || session.workout_type || "Workout";
          const duration = session.duration_minutes;
          const date = session.created_at
            ? new Date(session.created_at).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : null;
          const totalDur = session.plan_data ? calculateTotalDuration(session.plan_data) : 0;
          const formattedDur = totalDur > 0 ? formatDuration(totalDur) : (duration ? `${duration}m` : null);

          return (
            <button
              key={session.id}
              onClick={() => handleLoadSession(session)}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/60 border border-border/50 hover:border-border transition-colors text-left group"
            >
              <Dumbbell className="h-5 w-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-white block truncate">
                  {label}
                </span>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  {formattedDur && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formattedDur}
                    </span>
                  )}
                  {date && <span>{date}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => handleToggleFavourite(e, session)}
                  className="p-1.5 rounded hover:bg-white/10 transition-colors"
                  title={session.is_favourite ? "Remove from favourites" : "Add to favourites"}
                >
                  <Star
                    className={`h-4 w-4 ${session.is_favourite ? "fill-yellow-500 text-yellow-500" : "text-muted-foreground"}`}
                  />
                </button>
                <button
                  onClick={(e) => handleDeleteSession(e, session)}
                  className="p-1.5 rounded hover:bg-destructive/20 transition-colors"
                  title="Delete session"
                >
                  <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                </button>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Load session</DialogTitle>
          <DialogDescription>
            Load a previous session, a favourite, or paste a copied session plan.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="history" className="flex-1">
              <History className="h-4 w-4 mr-1.5" />
              History
            </TabsTrigger>
            <TabsTrigger value="favourites" className="flex-1">
              <Star className="h-4 w-4 mr-1.5" />
              Favourites
            </TabsTrigger>
            <TabsTrigger value="clipboard" className="flex-1">
              <ClipboardPaste className="h-4 w-4 mr-1.5" />
              Clipboard
            </TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="mt-4">
            <SessionList items={historySessions} />
          </TabsContent>

          <TabsContent value="favourites" className="mt-4">
            <SessionList items={favouriteSessions} />
          </TabsContent>

          <TabsContent value="clipboard" className="mt-4 space-y-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePasteFromClipboard}
              className="w-full"
            >
              <ClipboardPaste className="mr-2 h-4 w-4" />
              Paste from clipboard
            </Button>
            <Textarea
              value={clipboardText}
              onChange={(e) => setClipboardText(e.target.value)}
              placeholder="Paste a session plan here…"
              className="min-h-[200px] font-mono text-xs resize-none"
              rows={10}
            />
            {clipboardError && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {clipboardError}
              </div>
            )}
            {clipboardValid && (
              <div className="flex items-center gap-2 text-sm text-green-500">
                <Check className="h-4 w-4 shrink-0" />
                Valid session plan detected!
              </div>
            )}
            <Button
              onClick={handleLoadFromClipboard}
              disabled={!clipboardValid}
              className="w-full"
            >
              Load session
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default LoadSessionDialog;
